import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ProductOnUser, ProductsList } from 'common-ts/dist/models/Product';
import { CartState, CartStateItem, RegularSizeValue, SizeType, ProductSpotlight, RegularSizeValueShortName } from 'common-ts/dist/models/Cart';
import axios, { AxiosRequestConfig } from 'axios';
import { RootState } from '../store';
import { OrderItem } from 'common-ts/dist/models/Orders';
import { BodyMeasurements, BodyMeasurementsForSize, BodyMeasurementsKey, CalculateClothBodyMeasurementsFromBodyMeasurements, CalculateClothingMeasurementsFromBodyMeasurements, ClothingType, FitType, UserBodyMeasurements, UserClothTypeSpecificBodyMeasurements, UserPersonalSizeInput } from 'common-ts/dist/models/UserMeasurements';
import { Address } from 'common-ts/dist/models/Address';
import { Mixpanel } from '../mixpanel';
import Coupon from 'common-ts/dist/models/Coupon';
import { calculateShippingSubsidyClient, ensureSnapLoaded, getStoreShippingConfig, ShippingSubsidyItem, terbebasClient } from '../terbebasApi';
import hash from 'object-hash';
import { loadMeasurementsFromLocalStorage, postUserPersonalSizeInput } from './MeasurementsRedux';
import { APIRequestStatus } from 'common-ts/dist/models/APIRequestStatus';
import { sendWebsiteEventMessage } from '../telegrambotevents';
import { setRedirectLink } from './MailingPreferenceRedux';
import { firstValidPhone } from 'common-ts/dist/models/Phone';

export const retryGetRequest = async (config: AxiosRequestConfig,  maxRetries: number) => {
  let result = null;
  var retries: number = 0;

  while (retries < maxRetries) {
    try {
      result = await axios(config);
      break;
    } catch (error) {
      console.log("Retry number: " + retries);
    }
    retries = retries + 1;
  }
  
  if (retries >= maxRetries) {
    console.log(`Too many  request retries.`)
  }
  return result;
}

export const PHONE_MISSING_CHECKOUT =
  'Nomor HP penerima belum diisi atau tidak valid. Lengkapi nomor HP pada alamat pengiriman.';

// Safely read the persisted coupon. localStorage may hold the literal string
// "undefined" (from an older bug) or malformed JSON — JSON.parse would throw at
// module scope and white-screen the app, so guard and self-heal.
function readStoredCoupon(): Coupon | undefined {
  try {
    const raw = localStorage.getItem('coupon');
    if (!raw || raw === 'undefined') return undefined;
    const parsed = JSON.parse(raw);
    return parsed && parsed.couponKey ? parsed : undefined;
  } catch {
    localStorage.removeItem('coupon');
    return undefined;
  }
}

// Define the initial state using that type
const initialState: CartState = {
  cartProductList: 
    localStorage.getItem('cartProductList') !== null ? 
    JSON.parse(localStorage.getItem('cartProductList') as string) : 
    [],
  addressName: undefined,
  address: undefined,
  kodePos: undefined,
  provinsi: undefined,
  kabupaten: undefined,
  kecamatan: undefined,
  kotaKecamatanText: undefined,
  keterangan: undefined,
  phoneNumber: undefined,
  destinationObject: undefined,
  destinationKiriminAjaObject: undefined,
  courierSelectionObject: undefined,
  courierWeight: undefined,
  insuranceSelected: false,
  shipmentPrice: undefined,
  shipmentPricePaid: undefined,
  estimasiPengiriman: undefined,
  couponKey: readStoredCoupon()?.couponKey,
  coupon: readStoredCoupon(),
  orderStatus: 'unsubmitted',
  orderData: undefined,
  userTriggerCheckout: false,

  // Livestream Discounts
  productSpotlightList: [],

  cartProductListHash: '',
  canAddItemDict: {},

  courierOptions: undefined,
  courierOptionsFetchStatus: APIRequestStatus.Idle,
  
  couponFetchStatus: APIRequestStatus.Idle,
  couponError: undefined,
  phoneError: undefined,

  paymentToken: "",
  paymentTokenFetchStatus: APIRequestStatus.Idle,
}

function getCookieValueByName(name: string) {
  var match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? match[2] : "";
}

// The purchasable lines, in the shape both the courier quote and the order
// endpoint take. Shared so the parcel the server prices is the parcel it later
// ships — legacy measurement-only cart rows carry no variant and are skipped.
export function getCheckoutLineItems(cart: CartState) {
  return cart.cartProductList
    .filter((item) => item.variantId)
    .map((item) => ({ variantId: item.variantId, quantity: item.amount }));
}

// Creates the terbebas order from the cart + address in cart state, then
// requests a Midtrans Snap token. Returns the shape the legacy reducers
// expect ({creationDateTime, transactionToken}) so the stepper flow,
// rejected screen, and payment screen keep working unchanged.
export const submitOrderRequest = createAsyncThunk(
  'cart/submitOrderRequest',
  async (payload, thunkAPI) => {
    const rootState = (thunkAPI.getState() as RootState);
    const idToken = rootState.account.idToken;
    const cart = rootState.cart;

    if (cart.shipmentPrice === undefined) {
      console.log("ERROR! SHIPMENT PRICE HAS TO ALWAYS EXIST BEFORE THIS IS CALLED");
      return {};
    }

    const items = getCheckoutLineItems(cart);
    if (!items.length) {
      return {};
    }

    // Re-check the coupon before creating the order, so a coupon gone stale
    // between apply and checkout is caught HERE — dropped with a reason, cart
    // kept — instead of failing order creation (which empties the cart).
    if (cart.couponKey) {
      try {
        await terbebasClient.get('/storefront/coupons/' + encodeURIComponent(cart.couponKey),
          idToken ? { headers: { 'Authorization': 'Bearer ' + idToken } } : undefined);
      } catch (error: any) {
        return {
          couponRejected: true,
          message: error?.response?.data?.error ?? 'Kode promo tidak berlaku lagi',
        };
      }
    }

    // Buyer phone = the recipient's: the phone on the checkout address first
    // (typed by a guest, or copied from the saved address a signed-in buyer
    // picked), then the address draft, then the account's own phone. The
    // old code used the account phone ONLY, which is empty for returning
    // buyers who signed in by email — every such order was stored with the
    // bare prefix "62". If nothing valid exists we stop here (cart kept) and
    // send the buyer back to the address step rather than submit garbage.
    const customerNumber = firstValidPhone([
      cart.phoneNumber,
      rootState.addressList.addressDraft.phoneNumber,
      rootState.account.phoneNumber,
    ]);
    if (!customerNumber) {
      return { phoneRejected: true, message: PHONE_MISSING_CHECKOUT };
    }

    try {
      const orderResponse = await terbebasClient.post('/storefront/orders', {
        items,
        email: rootState.account.email || undefined,
        phone: customerNumber,
        customerName: rootState.account.name,
        shippingAddress: {
          recipientName: cart.addressName ?? rootState.account.name,
          phone: customerNumber,
          province: cart.provinsi ?? '',
          city: cart.kabupaten ?? '',
          district: cart.kecamatan ?? '',
          postalCode: cart.kodePos !== undefined ? String(cart.kodePos) : '',
          addressDetail: [cart.address, cart.keterangan].filter(Boolean).join(' — '),
          kiriminAjaDestination: cart.destinationKiriminAjaObject ?? undefined,
          courierSelection: cart.courierSelectionObject ?? undefined,
          estimasiPengiriman: cart.estimasiPengiriman ?? undefined,
        },
        // The raw quoted rate — the server applies the store's shipping
        // rules (free-above/subsidy/flat) and coupon free-shipping on top,
        // mirroring what getEndShipmentPrice displayed.
        shippingCost: cart.shipmentPrice,
        // Shopper's "Pakai Asuransi" choice (or a courier that forces it).
        // The server re-gates on the store's insuranceEnabled opt-in.
        shippingInsured: !!(cart.insuranceSelected || cart.courierSelectionObject?.force_insurance),
        couponCode: cart.couponKey || undefined,
        note: cart.keterangan ?? '',
      }, idToken ? { headers: { 'Authorization': 'Bearer ' + idToken } } : undefined);

      const order = orderResponse.data;

      // Snap token fetch may fail (e.g. store only takes manual bank
      // transfer) — the order already exists, so continue to the payment
      // screen without a token instead of rejecting.
      let payData: { token?: string; redirectUrl?: string } = {};
      try {
        const payResponse = await terbebasClient.post(
          '/storefront/orders/' + order.id + '/pay', {});
        await ensureSnapLoaded(payResponse.data.clientKey, payResponse.data.isProduction);
        payData = payResponse.data;
      } catch (payError: any) {
        console.log('Snap token unavailable:', payError?.response?.data?.error);
      }

      return {
        ...order,
        orderId: order.id,
        creationDateTime: order.createdAt,
        transactionToken: payData.token,
        redirectUrl: payData.redirectUrl,
      };
    } catch (error: any) {
      console.log('Order submit failed:', error?.response?.data?.error);
      return { message: error?.response?.data?.error ?? 'Order gagal dibuat' };
    }
  });

// Terbebas: legacy fareast order-cancel — no-op (unpaid orders simply
// stay PENDING; abandoned Snap transactions expire on their own).
export const cancelUserOrders = createAsyncThunk('cart/cancelUserOrders', async (arg, thunkAPI) => {
  return {};
})

// Terbebas: legacy fareast WhatsApp customer-notify — no-op (per-store
// notifications come later).
export const postCustomerNotification = createAsyncThunk('cart/postCustomerNotification', async (arg: any, thunkAPI) => {
  return {};
})

export const getCurrentOrder = createAsyncThunk('cart/getCurrentOrder', async (payload, thunkAPI) => {
  const rootState = (thunkAPI.getState() as RootState);
  const orderData = rootState.cart.orderData;

  const response = await terbebasClient.get('/storefront/orders/' + (orderData?.orderId ?? orderData?.id));
  const order = response.data;

  // Legacy reducer switches on Midtrans-style paymentStatus strings.
  const paymentStatus =
    order.financialStatus === 'PAID' ? 'settlement' :
    order.financialStatus === 'PENDING' ? 'pending' :
    'abandoned';

  return { ...order, paymentStatus };
})

// Terbebas coupon lookup ("Kode promo"). Maps the Discount COUPON row onto
// the legacy fareast coupon shape the reducers/invoice math already
// understand: amountOff -> PRICECUT, percentOff -> PRICECUTPERCENT, plus
// the freeShipping/minSubtotal extras handled by the shipping helpers.
export const submitCouponRequest = createAsyncThunk('cart/submitCouponRequest', async (payload: string, thunkAPI) => {
  const code = (payload ?? '').trim().toUpperCase();
  // Send the shopper's token so the server can check the per-account limit;
  // without it a signed-in shopper reads as a guest and the cap never fires.
  const idToken = (thunkAPI.getState() as RootState).account.idToken;

  let response;
  try {
    response = await terbebasClient.get('/storefront/coupons/' + encodeURIComponent(code),
      idToken ? { headers: { 'Authorization': 'Bearer ' + idToken } } : undefined);
  } catch (error: any) {
    // 400/404 (expired, exhausted, per-account limit, not found) reject in
    // axios — surface the server's Indonesian message to the cart.
    return {
      isUsable: false, couponKey: code, coupon: undefined,
      errorMessage: error?.response?.data?.error ?? 'Gagal memeriksa kode promo. Coba lagi.',
    };
  }

  const apiCoupon = response.data?.coupon;

  const couponEffects = [
    ...(apiCoupon.amountOff ? [{ effectKey: 'PRICECUT', effectValue: apiCoupon.amountOff }] : []),
    ...(apiCoupon.percentOff ? [{ effectKey: 'PRICECUTPERCENT', effectValue: apiCoupon.percentOff / 100 }] : []),
  ];

  return {
    isUsable: true,
    couponKey: apiCoupon.code,
    coupon: {
      couponKey: apiCoupon.code,
      // The legacy reducers gate every effect on expirationDateTime.
      expirationDateTime: apiCoupon.endsAt ?? '2999-12-31T00:00:00.000Z',
      maxRedeems: 0,
      couponConditions: [],
      couponEffects,
      freeShipping: !!apiCoupon.freeShipping,
      minSubtotal: apiCoupon.minSubtotal ?? undefined,
      couponNotes: apiCoupon.title,
    },
  };
})

export const paymentTokenFetch = createAsyncThunk('cart/paymentTokenFetch', async (payload: {
  orderId: string,
  enabledPayments: string[] | undefined,
}, thunkAPI) => {
  const response = await terbebasClient.post('/storefront/orders/' + payload.orderId + '/pay',
    payload.enabledPayments ? { enabledPayments: payload.enabledPayments } : {});
  await ensureSnapLoaded(response.data.clientKey, response.data.isProduction);
  return {
    success: !!response.data.token,
    transactionToken: response.data.token,
  };
})

export const getCourierOptions = createAsyncThunk('cart/getCourierOptions', async (arg: { dest: number }, thunkAPI) => {
  // Insurance rides on cart state (not the arg) so every call site — address
  // step and the courier-page toggle — quotes with the current preference.
  //
  // Only the line items go over the wire: the server derives the parcel's
  // weight, dimensions and declared value from the catalog, and rebuilds the
  // identical parcel when it books the pickup. Quoting a weight from here is
  // what let the two drift apart and failed KiriminAja's UAT.
  const rootState = thunkAPI.getState() as RootState;
  const insured = getStoreShippingConfig().insuranceEnabled === true
    && rootState.cart.insuranceSelected === true;
  const response = await terbebasClient.post('/storefront/shipping/rates', {
    destinationDistrictId: arg.dest,
    items: getCheckoutLineItems(rootState.cart),
    insurance: insured,
  });
  // Legacy reducer reads {success, returnBody: {results}}.
  return { success: response.data?.status === true, returnBody: response.data };
})

export const getShipmentPrice = createAsyncThunk('cart/getShipmentPrice', async (destination: any) => {
  var data = {
    "origin":"CGK",
    "destination":destination.destination_code,
    "weight":"0.8",
    "p":"30",
    "l":"20",
    "t":"3",
  };

  var config: AxiosRequestConfig = {
    method: 'post',
    url: 'https://content-main-api-production.sicepat.com/public/delivery-fee/fare-non-international',
    headers: { 
      'authority': 'content-main-api-production.sicepat.com', 
      'pragma': 'no-cache', 
      'cache-control': 'no-cache', 
      'sec-ch-ua': '"Google Chrome";v="93", " Not;A Brand";v="99", "Chromium";v="93"', 
      'accept': 'application/json, text/plain, */*', 
      'content-type': 'application/json;charset=UTF-8', 
      'sec-ch-ua-mobile': '?0', 
      'user-agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/93.0.4577.82 Safari/537.36', 
      'sec-ch-ua-platform': '"macOS"', 
      'origin': 'https://www.sicepat.com', 
      'sec-fetch-site': 'same-site', 
      'sec-fetch-mode': 'cors', 
      'sec-fetch-dest': 'empty', 
      'referer': 'https://www.sicepat.com/', 
      'accept-language': 'en-US,en;q=0.9,id-ID;q=0.8,id;q=0.7'
    },
    data: data
  };

  const response = await axios(config);

  const regularShipment = response.data.sicepat.results.find((item: any) => item.service === "REG");

  sendWebsiteEventMessage("Getting shipment estimate SiCepat" + JSON.stringify(regularShipment));

  return {
    regularShipment: regularShipment,
    destinationObject: destination,
  }
})

export function getMeasurementForCartStateItem(cartStateItemObject: CartStateItem,
  userBodyMeasurementsDict: {[key: string]: UserBodyMeasurements[]}, 
  userClothingMeasurementsDict: {[key: string]:{[key: string]: UserClothTypeSpecificBodyMeasurements[]};}) {

  const key2 = cartStateItemObject.clothingType + "-" + cartStateItemObject.fitType;
  var measurementsForItem: BodyMeasurements | undefined;
  if (cartStateItemObject.sizeType === 'personal') {
    if (cartStateItemObject.measurementIsBodyMeasurement) {
      // USE LATEST INSTEAD OF CORRECT VERSION
      let selectedObjectUpdateDateTime = userBodyMeasurementsDict[cartStateItemObject.measurementCreationDateTime][0];
      // for (let candidateObject of userBodyMeasurementsDict[cartStateItemObject.measurementCreationDateTime]) {
      //   if (candidateObject.updateDateTime === cartStateItemObject.measurementUpdateDateTime) {
      //     selectedObjectUpdateDateTime = candidateObject;
      //   }
      // }

      measurementsForItem = CalculateClothBodyMeasurementsFromBodyMeasurements(cartStateItemObject.clothingType, 
        cartStateItemObject.fitType, 
        selectedObjectUpdateDateTime.adjustedMeasurements as any, RegularSizeValue.L, []);

      if (measurementsForItem === undefined) {
        measurementsForItem = CalculateClothBodyMeasurementsFromBodyMeasurements(cartStateItemObject.clothingType, 
          cartStateItemObject.fitType, 
          selectedObjectUpdateDateTime.generatedMeasurements as any, RegularSizeValue.L, []);
      }
    } else {
      let selectedObjectUpdateDateTime = userClothingMeasurementsDict[key2][cartStateItemObject.measurementCreationDateTime][0];
      for (let candidateObject of userClothingMeasurementsDict[key2][cartStateItemObject.measurementCreationDateTime]) {
        if (candidateObject.updateDateTime === cartStateItemObject.measurementUpdateDateTime) {
          selectedObjectUpdateDateTime = candidateObject;
        }
      }
      measurementsForItem = selectedObjectUpdateDateTime.adjustedMeasurements;

      if (measurementsForItem === undefined) {
        measurementsForItem = selectedObjectUpdateDateTime.generatedMeasurements;
      }
    }
  } else if (cartStateItemObject.sizeType === 'regular') {
    measurementsForItem = BodyMeasurementsForSize[cartStateItemObject.regularSize];
  }

  if (measurementsForItem === undefined) {
    measurementsForItem = {};
  }

  measurementsForItem = {...measurementsForItem};

  // BACKWARDS COMPATIBILITY FIX - Has panjang lengan pendek but not lebar lengan pendek (INCOMPLETE CB)
  if (measurementsForItem['panjangLenganPendek'] && measurementsForItem['lebarLenganPendek'] === undefined) {
    measurementsForItem = CalculateClothBodyMeasurementsFromBodyMeasurements(cartStateItemObject.clothingType, cartStateItemObject.fitType,
      measurementsForItem, RegularSizeValue.L, [BodyMeasurementsKey.lebarLenganPendek]);
  }

  return measurementsForItem;
}

export function getRequestDataProducts(cartProductList: CartStateItem[], 
  userBodyMeasurementsDict: {[key: string]: UserBodyMeasurements[]}, 
  userClothingMeasurementsDict: {[key: string]:{[key: string]: UserClothTypeSpecificBodyMeasurements[]};}) {

  var requestDataProducts = [];

  for (var i = 0; i < cartProductList.length; i = i + 1) {
    var cartStateItemObject = cartProductList[i];

    for (var j = 0; j < cartProductList[i].amount; j = j + 1) {
      requestDataProducts.push({
        resource: "PRODUCT",
        resourceId: cartStateItemObject.productId, 
        provisionStrategy: "PRODUCT",
        provisionStrategyMetadata: {
          productId: cartStateItemObject.productId,
          measurements: getMeasurementForCartStateItem(cartStateItemObject, userBodyMeasurementsDict, userClothingMeasurementsDict),
          measurementsSizeShortName: cartStateItemObject.sizeType === SizeType.regular ? RegularSizeValueShortName?.[cartStateItemObject.regularSize] : '',
        }
      });
    }
  }

  return requestDataProducts;
}

// Terbebas: availability is answered locally from variant inventory in the
// loaded catalog (the checkout endpoint re-validates authoritatively).
function productHasPurchasableVariant(rootState: RootState, productId: string): boolean {
  const product = rootState.productsList.productsDictionary[productId];
  const variants = product?.commerce?.variants ?? [];
  if (!product) return false;
  if (!variants.length) return !product.isSoldOut;
  return variants.some((v) =>
    !v.inventoryTracked || v.inventoryPolicy === 'CONTINUE' || v.inventoryQuantity > 0
  );
}

export const postCheckCurrentCartAvailability = createAsyncThunk('cart/postCheckCurrentCartAvailability', async (payload: {}, thunkAPI) => {

  const rootState = (thunkAPI.getState() as RootState);
  const cartProductList = rootState.cart.cartProductList;

  // Never judge availability before the catalog has loaded — a false
  // negative here wipes the customer's cart (see the fulfilled reducer).
  const catalogLoaded = rootState.productsList.status === APIRequestStatus.Success;

  const canBeProvisioned = !catalogLoaded || cartProductList.every((item) =>
    productHasPurchasableVariant(rootState, item.productId)
  );

  return {
    canBeProvisioned,
  }
})

export const postCanAddItemCount = createAsyncThunk('cart/postCanAddItemCount', async (payload: CartStateItem, thunkAPI) => {

  const rootState = (thunkAPI.getState() as RootState);
  const hashKey = hash(payload);

  // Terbebas: answered locally from the loaded catalog's variant inventory.
  // The checkout endpoint re-validates stock authoritatively at order time.
  // Optimistic while the catalog is still loading.
  const catalogLoaded = rootState.productsList.status === APIRequestStatus.Success;
  const canBeProvisioned = !catalogLoaded || productHasPurchasableVariant(rootState, payload.productId);

  return {
    cartStateHash: hashKey,
    canBeProvisioned,
    isServiceable: true,
    currentMeasurements: {},
    serviceableMeasurements: {},
  }
})

export const loadAllIframeVariablesFromLocalStorage = createAsyncThunk('cart/loadAllIframeVariablesFromLocalStorage', async (payload, thunkAPI) => {
  thunkAPI.dispatch(loadCartFromLocalStorage());
  thunkAPI.dispatch(loadMeasurementsFromLocalStorage());
});

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addItemCount: (state, action: PayloadAction<{ productId: string,
      productDetail: ProductOnUser, fitType: FitType, sizeType: SizeType,
      measurementCreationDateTime: string, measurementUpdateDateTime: string, measurementIsBodyMeasurement: boolean, regularSize: RegularSizeValue,
      variantId?: string, variantTitle?: string, unitPrice?: number, compareAtPrice?: number | null }>) => {
      const { productId, productDetail, fitType, sizeType, measurementCreationDateTime, measurementUpdateDateTime, measurementIsBodyMeasurement, regularSize,
        variantId, variantTitle, unitPrice, compareAtPrice } = action.payload;
      if (measurementCreationDateTime === '') {
        return;
      }
      var cartStateItem: CartStateItem = {
        productId: productId,
        amount: 1,
        clothingType: productDetail.clothingType,
        fitType: fitType,
        sizeType: sizeType,
        measurementCreationDateTime: measurementCreationDateTime,
        measurementUpdateDateTime: measurementUpdateDateTime,
        measurementIsBodyMeasurement: measurementIsBodyMeasurement,
        regularSize: regularSize,
        ...(variantId !== undefined ? { variantId, variantTitle, unitPrice, compareAtPrice } : {}),
      }
      if (canAddProductAmountToCart(state, cartStateItem) !== "ENOUGH") {
        console.log("Can't add product to cart: not enough.")
        return;
      }
      const existingCartItem = state.cartProductList
      .find(item => item.productId === productId
        && item.fitType === fitType
        && item.sizeType === sizeType
        && item.measurementCreationDateTime === measurementCreationDateTime
        && item.measurementUpdateDateTime === measurementUpdateDateTime
        && item.measurementIsBodyMeasurement === measurementIsBodyMeasurement
        && item.regularSize === regularSize
        && item.variantId === variantId);
      if (existingCartItem) {
        existingCartItem.amount = existingCartItem.amount + 1;
      } else {
        state.cartProductList.push(cartStateItem);
      }
      localStorage.setItem('cartProductList', JSON.stringify(state.cartProductList));

      // Reset add item dict every time we add a new item
      state.canAddItemDict = {};
      state.cartProductListHash = hash(state.cartProductList);
    },
    removeItemCount: (state, action: PayloadAction<{productId: string,
      fitType: FitType, sizeType: SizeType,
      measurementCreationDateTime: string, measurementUpdateDateTime: string, regularSize: RegularSizeValue,
      variantId?: string}>) => {
      const { productId, measurementCreationDateTime, measurementUpdateDateTime, fitType, sizeType, regularSize, variantId } = action.payload;
      const existingCartItem = state.cartProductList
      .find(item => item.productId === productId
        && item.fitType === fitType
        && item.sizeType === sizeType
        && item.measurementCreationDateTime === measurementCreationDateTime
        && item.measurementUpdateDateTime === measurementUpdateDateTime
        && item.regularSize === regularSize
        && item.variantId === variantId);
      if (existingCartItem) {
        existingCartItem.amount = existingCartItem.amount - 1;
        if (existingCartItem.amount === 0) {
          state.cartProductList = state.cartProductList.filter(item => !(item.productId === productId
            && item.sizeType === sizeType
            && item.measurementCreationDateTime === measurementCreationDateTime
            && item.measurementUpdateDateTime === measurementUpdateDateTime
            && item.regularSize === regularSize
            && item.variantId === variantId));
        }
      }
      localStorage.setItem('cartProductList', JSON.stringify(state.cartProductList));

      // Reset add item dict every time we add a new item
      state.canAddItemDict = {};
      state.cartProductListHash = hash(state.cartProductList);
    },
    removeItem: (state, action: PayloadAction<{productId: string,
      fitType: FitType, sizeType: SizeType,
      measurementCreationDateTime: string, measurementUpdateDateTime: string, regularSize: RegularSizeValue,
      variantId?: string}>) => {
      const { productId, measurementCreationDateTime, measurementUpdateDateTime, fitType, sizeType, regularSize, variantId } = action.payload;
      state.cartProductList = state.cartProductList.filter(item => !(item.productId === productId
        && item.fitType === fitType
        && item.sizeType === sizeType
        && item.measurementCreationDateTime === measurementCreationDateTime
        && item.measurementUpdateDateTime === measurementUpdateDateTime
        && item.regularSize === regularSize
        && item.variantId === variantId));
      localStorage.setItem('cartProductList', JSON.stringify(state.cartProductList));

      // Reset add item dict every time we add a new item
      state.canAddItemDict = {};
      state.cartProductListHash = hash(state.cartProductList);
    },
    updateAddressInOrder: (state, action: PayloadAction<Address>) => {
      Object.assign(state, action.payload);
      state.phoneError = undefined;
    },
    updateCourierInOrder: (state, action: PayloadAction<{ 
      shipmentPrice: number, 
      shipmentPricePaid: number, 
      estimasiPengiriman: string, 
      courierSelectionObject: any,
      // Vestigial fareast field, written by nothing that reads it. The parcel's
      // real weight now lives server-side (computeShipmentPackage), so callers
      // deliberately no longer supply an estimate here.
      courierWeight?: number,
    }>) => {
      Object.assign(state, action.payload);
    },
    resetCourier: (state) => {
      state.shipmentPrice = undefined;
      state.shipmentPricePaid = undefined;
      state.estimasiPengiriman = undefined;
      state.courierSelectionObject = undefined;
      state.courierWeight = undefined;
      // insuranceSelected deliberately survives — going back to the address
      // step shouldn't forget the shopper's insurance preference.
    },
    // Flipping "Pakai Asuransi" invalidates every quoted price, so the
    // current selection is cleared alongside — a stale uninsured price can
    // never be submitted as insured (PILIH stays disabled until reselect).
    setInsuranceSelected: (state, action: PayloadAction<boolean>) => {
      state.insuranceSelected = action.payload;
      state.shipmentPrice = undefined;
      state.shipmentPricePaid = undefined;
      state.estimasiPengiriman = undefined;
      state.courierSelectionObject = undefined;
      state.courierWeight = undefined;
    },
    resetCartState: (state) => {
      state.cartProductList = [];
      state.orderData = undefined;
      state.orderStatus = 'unsubmitted';
      state.addressName = undefined;
      state.address = undefined;
      state.provinsi = undefined;
      state.kabupaten = undefined;
      state.kecamatan = undefined;
      state.kodePos = undefined;
      state.kotaKecamatanText = undefined;
      state.keterangan = undefined;
      state.phoneNumber = undefined;
      state.destinationObject = undefined;
      state.destinationKiriminAjaObject = undefined;
      state.courierSelectionObject = undefined;
      state.courierWeight = undefined;
      state.insuranceSelected = false;
      state.shipmentPrice = undefined;
      state.shipmentPricePaid = undefined;
      state.estimasiPengiriman = undefined;
      state.couponKey = undefined;
      state.coupon = undefined;

      localStorage.setItem('cartProductList', JSON.stringify(state.cartProductList));

      // Reset add item dict every time we add a new item
      state.canAddItemDict = {};
      state.cartProductListHash = hash(state.cartProductList);
    },
    resetCartStateAndSetOrderStatus: (state, action: PayloadAction<"unsubmitted" | "waitingForPayment" 
    | "verifyPaymentWithServer" | "success" | "paymentFailed" | "rejected">) => {
      state.cartProductList = [];
      state.orderData = undefined;
      state.orderStatus = action.payload;
      state.addressName = undefined;
      state.address = undefined;
      state.provinsi = undefined;
      state.kabupaten = undefined;
      state.kecamatan = undefined;
      state.kodePos = undefined;
      state.kotaKecamatanText = undefined;
      state.keterangan = undefined;
      state.phoneNumber = undefined;
      state.destinationObject = undefined;
      state.destinationKiriminAjaObject = undefined;
      state.courierSelectionObject = undefined;
      state.courierWeight = undefined;
      state.insuranceSelected = false;
      state.shipmentPrice = undefined;
      state.shipmentPricePaid = undefined;
      state.estimasiPengiriman = undefined;
      state.couponKey = undefined;
      state.coupon = undefined;

      localStorage.setItem('cartProductList', JSON.stringify(state.cartProductList));

      // Reset add item dict every time we add a new item
      state.canAddItemDict = {};
      state.cartProductListHash = hash(state.cartProductList);
    },
    resetCartAddressAndPaymentState: (state) => {
      state.orderData = undefined;
      state.orderStatus = 'unsubmitted';
      state.addressName = undefined;
      state.address = undefined;
      state.provinsi = undefined;
      state.kabupaten = undefined;
      state.kecamatan = undefined;
      state.kodePos = undefined;
      state.kotaKecamatanText = undefined;
      state.keterangan = undefined;
      state.phoneNumber = undefined;
      state.phoneError = undefined;
      state.destinationObject = undefined;
      state.destinationKiriminAjaObject = undefined;
      state.courierSelectionObject = undefined;
      state.courierWeight = undefined;
      state.shipmentPrice = undefined;
      state.shipmentPricePaid = undefined;
      state.estimasiPengiriman = undefined;
    },
    updateOrderState (state, action) {
      state.orderStatus = action.payload;
    },
    updateUserTrigger (state, action) {
      state.userTriggerCheckout = action.payload;
    },
    removeCurrentCoupon (state) {
      state.couponKey = undefined;
      state.coupon = undefined;
      state.couponError = undefined;
      localStorage.removeItem('coupon');
    },
    removePaymentToken(state) {
      state.paymentToken = "";
    },
    setProductSpotlightList(state, action) {
      state.productSpotlightList = action.payload;
    },
    setLocalCoupon(state, action) {
      state.couponKey = action.payload.couponKey;
      state.coupon = action.payload.coupon;

      localStorage.setItem('coupon', JSON.stringify(state.coupon));
    },
    loadCartFromLocalStorage: (state) => {
      const storedCart = localStorage.getItem('cartProductList');
      if (storedCart) {
        try {
          const parsed = JSON.parse(storedCart);
          state.cartProductList = parsed;
          state.cartProductListHash = hash(parsed);

          // Clear the availability cache because the base cart has changed.
          state.canAddItemDict = {};

        } catch (err) {
          console.error("Failed to parse stored cart:", err);
        }
      }
    }
  },
  extraReducers(builder) {
    builder
      .addCase(submitCouponRequest.pending, (state, action) => {
        state.couponFetchStatus = APIRequestStatus.RequestInProgress;
        state.couponError = undefined;
      })
      .addCase(submitCouponRequest.fulfilled, (state, action) => {
        Mixpanel.track('couponKeyValue', {
          'couponKey': action.payload.couponKey,
          'coupon': action.payload.coupon,
        })
        if (action.payload.isUsable) {
          state.couponFetchStatus = APIRequestStatus.Success;
          state.couponKey = action.payload.couponKey;
          state.coupon = action.payload.coupon;
          state.couponError = undefined;
          // Only persist a real coupon — writing `undefined` here stored the
          // string "undefined", which then crashed the app on next boot.
          localStorage.setItem('coupon', JSON.stringify(action.payload.coupon));
        } else {
          // Server rejected the coupon: drop it (and any stored copy) so a stale
          // discount is never priced into the cart, and show the reason.
          state.couponFetchStatus = APIRequestStatus.Failure;
          state.couponKey = undefined;
          state.coupon = undefined;
          state.couponError = action.payload.errorMessage;
          localStorage.removeItem('coupon');
        }
      })
      .addCase(submitCouponRequest.rejected, (state, action) => {
        // Genuine transport failure only — leave any existing coupon alone.
        state.couponFetchStatus = APIRequestStatus.Failure;
        state.couponError = 'Gagal memeriksa kode promo. Coba lagi.';
      })
      .addCase(getShipmentPrice.pending, (state, action) => {
      })
      .addCase(getShipmentPrice.fulfilled, (state, action) => {
        state.shipmentPrice = action.payload.regularShipment.tariff;
        state.estimasiPengiriman = action.payload.regularShipment.etd;
        state.destinationObject = action.payload.destinationObject;
        console.log(action.payload);
      })
      .addCase(getShipmentPrice.rejected, (state, action) => {
      })
      .addCase(submitOrderRequest.pending, (state, action) => {
      })
      .addCase(submitOrderRequest.fulfilled, (state, action) => {
        // Checking results of order request
        console.log("Check results of order request");
        if (action.payload.phoneRejected) {
          // No valid buyer phone: nothing was sent. Keep the cart; PageCheckout
          // routes the shopper back to the address step to fill it in.
          state.phoneError = action.payload.message;
          state.orderStatus = 'unsubmitted';
          return;
        }
        if (action.payload.couponRejected) {
          // Coupon-only failure caught before the order was created: drop the
          // coupon and show why, but KEEP the cart and stay on the checkout step
          // so the shopper can retry at full price. Never empties the cart.
          state.couponKey = undefined;
          state.coupon = undefined;
          state.couponError = action.payload.message;
          localStorage.removeItem('coupon');
          return;
        }
        if (action.payload.creationDateTime === undefined) {
          // Order creation has failed. Display error message, then redirect to home page.
          console.log("Order request is rejected");
          state.orderData = action.payload;
          state.orderStatus = 'rejected';
          // Stock ran out (or the order was otherwise refused) — the cart holds
          // items that can no longer be bought, so empty it. Same bookkeeping as
          // resetCartState, minus orderData so the rejection dialog keeps its message.
          state.cartProductList = [];
          localStorage.setItem('cartProductList', JSON.stringify(state.cartProductList));
          state.canAddItemDict = {};
          state.cartProductListHash = hash(state.cartProductList);
        } else {
          // Order created. transactionToken may be absent (manual bank
          // transfer only) — the payment screen handles both.
          state.orderData = action.payload;
          state.orderStatus = 'waitingForPayment';
        }
      })
      .addCase(submitOrderRequest.rejected, (state, action) => {
      })
      .addCase(getCurrentOrder.pending, (state, action) => {
      })
      .addCase(getCurrentOrder.fulfilled, (state, action) => {
        console.log("Received current order status.");
        console.log(action.payload);
        if (action.payload.paymentStatus === 'pending' 
        || action.payload.paymentStatus === 'capture'
        || action.payload.paymentStatus === 'settlement') {
          state.orderStatus = 'success';
          state.orderData = undefined;
          state.addressName = undefined;
          state.address = undefined;
          state.provinsi = undefined;
          state.kabupaten = undefined;
          state.kecamatan = undefined;
          state.kodePos = undefined;
          state.kotaKecamatanText = undefined;
          state.keterangan = undefined;
          state.phoneNumber = undefined;
          state.destinationObject = undefined;
          state.destinationKiriminAjaObject = undefined;
          state.courierSelectionObject = undefined;
          state.courierWeight = undefined;
          state.shipmentPrice = undefined;
          state.shipmentPricePaid = undefined;
          state.estimasiPengiriman = undefined;
          state.couponKey = undefined;
          state.coupon = undefined;
          state.cartProductList = [];

          localStorage.setItem('cartProductList', JSON.stringify(state.cartProductList));

          // Reset add item dict every time we add a new item
          state.canAddItemDict = {};
          state.cartProductListHash = hash(state.cartProductList);
        } else {
          console.log("PAYMENT ERROR");
          state.orderStatus = 'paymentFailed';
          state.orderData = undefined;
          state.addressName = undefined;
          state.address = undefined;
          state.provinsi = undefined;
          state.kabupaten = undefined;
          state.kecamatan = undefined;
          state.kodePos = undefined;
          state.kotaKecamatanText = undefined;
          state.keterangan = undefined;
          state.phoneNumber = undefined;
          state.destinationObject = undefined;
          state.destinationKiriminAjaObject = undefined;
          state.courierSelectionObject = undefined;
          state.courierWeight = undefined;
          state.shipmentPrice = undefined;
          state.shipmentPricePaid = undefined;
          state.estimasiPengiriman = undefined;
          state.couponKey = undefined;
          state.coupon = undefined;
          state.estimasiPengiriman = undefined;
        }
      })
      .addCase(getCurrentOrder.rejected, (state, action) => {
      })
      .addCase(postCanAddItemCount.pending, (state, action) => {
        var cartStateItemHash = hash(action.meta.arg);
        state.canAddItemDict[cartStateItemHash] = "LOADING";
      })
      .addCase(postCheckCurrentCartAvailability.fulfilled, (state, action) => {
        if (!action.payload.canBeProvisioned) {
          // If it is not available anymore, we reset the contents
          state.cartProductList = [];
          state.orderData = undefined;
          state.orderStatus = 'unsubmitted';
          state.addressName = undefined;
          state.address = undefined;
          state.provinsi = undefined;
          state.kabupaten = undefined;
          state.kecamatan = undefined;
          state.kodePos = undefined;
          state.kotaKecamatanText = undefined;
          state.keterangan = undefined;
          state.phoneNumber = undefined;
          state.destinationObject = undefined;
          state.destinationKiriminAjaObject = undefined;
          state.courierSelectionObject = undefined;
          state.courierWeight = undefined;
          state.shipmentPrice = undefined;
          state.shipmentPricePaid = undefined;
          state.estimasiPengiriman = undefined;
          state.couponKey = undefined;
          state.coupon = undefined;
    
          localStorage.setItem('cartProductList', JSON.stringify(state.cartProductList));
    
          // Reset add item dict every time we add a new item
          state.canAddItemDict = {};
          state.cartProductListHash = hash(state.cartProductList);

          console.log("Cart contents are unavailable. Emptying.");
        } else {
          console.log("Cart contents are safe / available. Carry on.");
        }
      })
      .addCase(postCheckCurrentCartAvailability.rejected, (state, action) => {
      })
      .addCase(postCheckCurrentCartAvailability.pending, (state, action) => {
        // We put the hash in, so it is not calculated / trigerred twice
        state.canAddItemDict = {};
        state.cartProductListHash = hash(state.cartProductList);
      })
      .addCase(postCanAddItemCount.fulfilled, (state, action) => {
        state.canAddItemDict[action.payload.cartStateHash] = {
          canBeProvisioned: action.payload.canBeProvisioned,
          isServiceable: action.payload.isServiceable,
          currentMeasurements: action.payload.currentMeasurements,
          serviceableMeasurements: action.payload.serviceableMeasurements,
        };
      })
      .addCase(postCanAddItemCount.rejected, (state, action) => {
      })
      .addCase(paymentTokenFetch.pending, (state, action) => {
        state.paymentTokenFetchStatus = APIRequestStatus.RequestInProgress;
      })
      .addCase(paymentTokenFetch.fulfilled, (state, action) => {
        state.paymentTokenFetchStatus = APIRequestStatus.Success;
        if (action.payload.success) {
          state.paymentToken = action.payload.transactionToken;
        }
      })
      .addCase(paymentTokenFetch.rejected, (state, action) => {
        state.paymentTokenFetchStatus = APIRequestStatus.Failure;
      })
      .addCase(postCustomerNotification.pending, (state, action) => {
      })
      .addCase(postCustomerNotification.fulfilled, (state, action) => {
      })
      .addCase(postCustomerNotification.rejected, (state, action) => {
      })
      .addCase(cancelUserOrders.pending, (state, action) => {
      })
      .addCase(cancelUserOrders.fulfilled, (state, action) => {
      })
      .addCase(cancelUserOrders.rejected, (state, action) => {
      })
      .addCase(getCourierOptions.pending, (state, action) => {
        state.courierOptionsFetchStatus = APIRequestStatus.RequestInProgress;
      })
      .addCase(getCourierOptions.fulfilled, (state, action) => {
        state.courierOptionsFetchStatus = APIRequestStatus.Success;
        
        if (action.payload?.success && action.payload?.returnBody?.results) {
          state.courierOptions = action.payload.returnBody.results;
        }
      })
      .addCase(getCourierOptions.rejected, (state, action) => {
        state.courierOptionsFetchStatus = APIRequestStatus.Failure;
      })
  }
})

export function getDiscountedPriceAndTimestampInProductSpotlightList(productSpotlightList: ProductSpotlight[], productId: string): 
{
  discountedPrice: number
  timestampLimit: string
  alternativeSource?: 'FLASHSALE' | 'COUPON' // By default assumes 'FLASHSALE'
} {
  const timestampCurrent = new Date().toISOString();

  // Get all active deals
  let activeProductSpotlights = productSpotlightList.filter((spotlightObj) => 
    spotlightObj.productId === productId &&
    timestampCurrent < spotlightObj.timestampLimit && 
    timestampCurrent > spotlightObj.timestampStart);

  // Find the best one
  let currentLowestPrice = 1000000000;
  let spotlightObjectValue: ProductSpotlight | undefined = undefined;

  for (let spotlightObj of activeProductSpotlights) {
    if (spotlightObj.discountedPrice < currentLowestPrice) {
      currentLowestPrice = spotlightObj.discountedPrice;
      spotlightObjectValue = spotlightObj;
    }
  }

  // Return results
  return {
    discountedPrice: spotlightObjectValue?.discountedPrice ?? -1,
    timestampLimit: spotlightObjectValue?.timestampLimit ?? '1999-10-10T00:00:00Z',
    alternativeSource: spotlightObjectValue?.alternativeSource
  }
}

export function checkIfMeasKitSizeExists(cartState: CartState, 
  userBodyMeasurementsDict: {[key:string]: UserBodyMeasurements[]} ) {

  let measKitSizeExists = false;

  for (let cartProductItem of cartState.cartProductList) {
    if (cartProductItem.sizeType === SizeType.personal
      && cartProductItem.measurementIsBodyMeasurement) {
      measKitSizeExists = true;
    }
  }

  return measKitSizeExists;
}

export interface CartItemsWithCouponPromotionsDict {
  [key:string]: {
    discountedPriceWithCouponPromotions: number
    amountDiscounted: number
  }
}

export function getCartItemsWithCouponPromotions(state: CartState, productListState: ProductsList, 
  userBodyMeasurementsDict: {[key:string]: UserBodyMeasurements[]}) {
  const cartItemsWithCouponPromotions: CartItemsWithCouponPromotionsDict = {};

  let mostExpensiveProductId = '';
  let mostExpensiveProductPrice = 0;

  // CALC ORIGINAL & SUBTOTAL AMOUNT
  for (let itemKey in state.cartProductList) {
    let item = state.cartProductList[itemKey];
    if (productListState.productsDictionary[item.productId] === undefined) {
      continue;
    }

    // Equals to -1 if not exist
    let defaultSalePrice = productListState.productsDictionary[item.productId].discountedPrice === -1 ? 
      productListState.productsDictionary[item.productId].originalPrice :
      productListState.productsDictionary[item.productId].discountedPrice;
  
    let combinedProductSpotlightList: ProductSpotlight[] = (state?.productSpotlightList?.length ? state.productSpotlightList : []).concat(state.coupon?.couponProductSpotlightList?.map((prodObj) => ({
      ...prodObj,
      timestampStart: '1999-10-10T00:00:00.000Z',
      timestampLimit: state.coupon?.expirationDateTime ?? '1999-10-10T00:00:00.000Z', 
      isMainSpotlight: false,
      alternativeSource: 'COUPON'
    })) ?? []);

    let spotlightDiscPrice = state.coupon?.couponBlastingEffects?.length ? Math.round((1 - state.coupon?.couponBlastingEffects?.[0].effectValue) * (productListState.productsDictionary[item.productId].originalPrice)) : combinedProductSpotlightList?.length ? getDiscountedPriceAndTimestampInProductSpotlightList(combinedProductSpotlightList, item.productId).discountedPrice : -1;

    if (spotlightDiscPrice !== -1) {
      continue; // Skip if flash sale price is applied
    }

    if (mostExpensiveProductPrice < defaultSalePrice) {
      mostExpensiveProductId = item.productId;
      mostExpensiveProductPrice = defaultSalePrice;
    }
  }

  // CALC DISC WITH PROMOTIONS AMOUNT
  if (state.couponKey && state.coupon) {
    // Check usability
    let couponUsable = true;

    if (state.coupon.expirationDateTime) {
      couponUsable = couponUsable && (new Date().toISOString() < state.coupon.expirationDateTime);
    }

    let allConditionsSatisfied = true;
    for (let couponConditionItem of state.coupon.couponConditions) {
      // if (couponConditionItem.conditionKey === "MEASKITSIZEEXISTS") {
      //   let measKitSizeExists = checkIfMeasKitSizeExists(state, userBodyMeasurementsDict);

      //   allConditionsSatisfied = allConditionsSatisfied && measKitSizeExists;
      // }
    }
    couponUsable = couponUsable && allConditionsSatisfied;

    // Use coupon
    if (couponUsable) {
      for (let key in state.coupon.couponEffects) {
        const couponEffect = state.coupon.couponEffects[key];
        if (couponEffect.effectKey === "SINGLEITEMDISCOUNT" && mostExpensiveProductId) {
          cartItemsWithCouponPromotions[mostExpensiveProductId] = {
            discountedPriceWithCouponPromotions: Math.round(mostExpensiveProductPrice * (1 - couponEffect.effectValue as number)),
            amountDiscounted: mostExpensiveProductPrice - Math.round(mostExpensiveProductPrice * (1 - couponEffect.effectValue as number)),
          }
        }
      }
    }
  }
  
  return cartItemsWithCouponPromotions;
}

export function GetBundlingKeyAmountDict(cartProductList: CartStateItem[], productListState: ProductsList) {
  const bundlingKeyAmountDict: {[key: string]: number } = {};

  for (let itemKey in cartProductList) {
    let item = cartProductList[itemKey];

    if (productListState.productsDictionary[item.productId]?.bundlingData) {
      const bundlingKey = productListState.productsDictionary[item.productId].bundlingData?.bundlingKey;
      if (bundlingKey) {
        bundlingKeyAmountDict[bundlingKey] = (bundlingKeyAmountDict?.[bundlingKey] ?? 0) + item.amount;
      }
    }
  }

  return bundlingKeyAmountDict;
}

export function CheckIsPlusSize(item: CartStateItem, 
  userBodyMeasurementsDict: {[key:string]: UserBodyMeasurements[]}, 
  userClothingMeasurementsDict: {[key: string]: {
      [key: string]: UserClothTypeSpecificBodyMeasurements[];
  }}) {
    
    // Resolve the measurement object based on type
    let measurementObject;
    if (item.sizeType === 'personal') {
        if (item.measurementIsBodyMeasurement) {
             // Look in BODY Dict
            // USE LATEST INSTEAD OF CORRECT VERSION
             measurementObject = userBodyMeasurementsDict[item.measurementCreationDateTime]?.[0];
            //  measurementObject = userBodyMeasurementsDict[item.measurementCreationDateTime]?.filter((measObj: any) => measObj.updateDateTime === item.measurementUpdateDateTime)?.[0];
        } else {
             // Look in CLOTHING Dict
             measurementObject = userClothingMeasurementsDict[item.clothingType + '-' + item.fitType]?.[item.measurementCreationDateTime]?.filter((measObj: any) => measObj.updateDateTime === item.measurementUpdateDateTime)?.[0];
        }
    }

    return (
      (item.sizeType === 'regular' && 
        (item.regularSize === RegularSizeValue.XXXL || item.regularSize === RegularSizeValue.XXXXL || item.regularSize === RegularSizeValue.XXXXXL || item.regularSize === RegularSizeValue.XXXXXXL || item.regularSize === RegularSizeValue.XXXXXXXL || item.regularSize === RegularSizeValue.XXXXXXXXL))
        ||
        (item.sizeType === 'personal' && 
        measurementObject?.adjustedMeasurements?.lingkarLeher &&
        (measurementObject.adjustedMeasurements.lingkarLeher as number) > 45)
    );
}

export function calculateCompleteCartInvoice(state: CartState, productListState: ProductsList, 
  userBodyMeasurementsDict: {[key:string]: UserBodyMeasurements[]}, 
  userClothingMeasurementsDict: {[key: string]: {
      [key: string]: UserClothTypeSpecificBodyMeasurements[];
  }}) {
  const resultObj: {
    cartItemsList: any[],
    totalAmountObj: {
      originalPriceAmount: number,
      discountedPriceAmount: number,
      discountedPriceAmountWithPromotions: number,
      discountedPriceAmountWithPromotionsAndShipment: number,
      volumeDiscountAmount: number,
    },
    cartItemsWithCouponPromotions: CartItemsWithCouponPromotionsDict
  } = {
    cartItemsList: [],
    totalAmountObj: {
      originalPriceAmount: 0,
      discountedPriceAmount: 0,
      discountedPriceAmountWithPromotions: 0,
      discountedPriceAmountWithPromotionsAndShipment: 0,
      volumeDiscountAmount: 0,
    },
    cartItemsWithCouponPromotions: {}
  }

  // BUNDLING
  const bundlingKeyAmountDict = GetBundlingKeyAmountDict(state.cartProductList, productListState);

  // CALC ORIGINAL & SUBTOTAL AMOUNT
  // ORIGINAL: ORIGINAL PRICE TOTAL
  // DEFAULT DISCOUNT: DISCOUNTS BEFORE ANY PROMOTIONS (NOT WITH SPOTLIGHT, BUT THIS INCLUDES BUNDLING)
  // SUBTOTAL: WITH PROMOTIONS TOTAL
  let originalTotal = 0;
  let defaultDiscTotal = 0;
  let subTotal = 0;
  for (let itemKey in state.cartProductList) {
    let item = state.cartProductList[itemKey];
    if (productListState.productsDictionary[item.productId] === undefined) {
      continue;
    }

    // Terbebas variant-priced item: totals come from the variant snapshot
    // (bundling/spotlight logic is product-level and does not apply).
    if (item.variantId && item.unitPrice !== undefined) {
      const variantOriginal = item.compareAtPrice ?? item.unitPrice;
      originalTotal = originalTotal + item.amount * variantOriginal;
      defaultDiscTotal = defaultDiscTotal + item.amount * item.unitPrice;
      subTotal = subTotal + item.amount * item.unitPrice;
      continue;
    }

    // PLUS SIZE CHARGE CHECK
    const isPlusSize = CheckIsPlusSize(item, userBodyMeasurementsDict, userClothingMeasurementsDict);
    if (isPlusSize) {
      const extraSizeChargePrice = (productListState.productsDictionary[item.productId]?.extraSizeChargePrice ?? 0);
      originalTotal = originalTotal + item.amount * extraSizeChargePrice;
      subTotal = subTotal + item.amount * extraSizeChargePrice;
      defaultDiscTotal = defaultDiscTotal + item.amount * extraSizeChargePrice;
    }

    originalTotal = originalTotal + item.amount * productListState.productsDictionary[item.productId].originalPrice;

    // Equals to -1 if not exist
    let defaultDiscPrice = productListState.productsDictionary[item.productId].discountedPrice === undefined ? -1 :
      productListState.productsDictionary[item.productId].discountedPrice;

    // BUNDLING PRICE EFFECT
    if (productListState.productsDictionary[item.productId]?.bundlingData) {
      const bundlingData = productListState.productsDictionary[item.productId].bundlingData;
      const bundlingKey = bundlingData?.bundlingKey;
      if (bundlingKey && bundlingKeyAmountDict[bundlingKey] > 1) {
        defaultDiscPrice = 
          bundlingKeyAmountDict[bundlingKey] >= 3 ? bundlingData.bundlingPrice3 :
          bundlingKeyAmountDict[bundlingKey] >= 2 ? bundlingData.bundlingPrice2 :
          defaultDiscPrice;
      }
    }
    
    let combinedProductSpotlightList: ProductSpotlight[] = (state?.productSpotlightList?.length ? state.productSpotlightList : []).concat(state.coupon?.couponProductSpotlightList?.map((prodObj) => ({
      ...prodObj,
      timestampStart: '1999-10-10T00:00:00.000Z',
      timestampLimit: state.coupon?.expirationDateTime ?? '1999-10-10T00:00:00.000Z', 
      isMainSpotlight: false,
      alternativeSource: 'COUPON'
    })) ?? []);

    // TODO: TEMPORARY FIX 
    let spotlightDiscPrice = state.coupon?.couponBlastingEffects?.length ? Math.round((1 - state.coupon?.couponBlastingEffects?.[0].effectValue) * (productListState.productsDictionary[item.productId].originalPrice)) : combinedProductSpotlightList?.length ? getDiscountedPriceAndTimestampInProductSpotlightList(combinedProductSpotlightList, item.productId).discountedPrice : -1;
    let thereIsDiscountedPrice = defaultDiscPrice !== -1 || spotlightDiscPrice !== -1;

    if (thereIsDiscountedPrice) {
      let discountedPrice = spotlightDiscPrice !== -1 ? spotlightDiscPrice : defaultDiscPrice;
      subTotal = subTotal + item.amount * discountedPrice;
    } else {
      subTotal = subTotal + item.amount * productListState.productsDictionary[item.productId].originalPrice;
    }

    defaultDiscTotal = defaultDiscTotal + item.amount * (defaultDiscPrice !== -1 ? defaultDiscPrice : productListState.productsDictionary[item.productId].originalPrice);
  }

  // PUT IN ORIGINAL & SUBTOTAL AMOUNT
  resultObj.totalAmountObj.originalPriceAmount = originalTotal;
  resultObj.totalAmountObj.discountedPriceAmount = defaultDiscTotal;

  // CHECK ADDITIONAL DISCOUNT FOR COUPON EFFECT SINGLEITEMDISCOUNT
  const cartItemsWithCouponPromotions = getCartItemsWithCouponPromotions(state, productListState, userBodyMeasurementsDict);
  resultObj.cartItemsWithCouponPromotions = cartItemsWithCouponPromotions;

  // Terbebas automatic volume/tier discounts (variant-priced items).
  // Applied BEFORE the coupon — the server computes the coupon amount on
  // the post-volume subtotal, so the order here must match.
  for (const discount of (productListState.activeDiscounts ?? [])) {
    const scope = new Set(discount.productIds);
    const matched = state.cartProductList.filter((i) => i.variantId && i.unitPrice !== undefined && scope.has(i.productId));
    const groupQty = matched.reduce((s, i) => s + i.amount, 0);
    if (!groupQty) continue;
    const tier = (discount.tiers ?? [])
      .filter((t) => t.minQty <= groupQty)
      .sort((a, b) => b.minQty - a.minQty)[0];
    if (!tier) continue;
    for (const item of matched) {
      const newUnit = tier.unitPrice !== undefined
        ? tier.unitPrice
        : (item.unitPrice as number) * (1 - (tier.percentOff ?? 0) / 100);
      const off = Math.max(0, (item.unitPrice as number) - newUnit) * item.amount;
      subTotal -= off;
      resultObj.totalAmountObj.volumeDiscountAmount += off;
    }
  }

  // CALC DISC WITH PROMOTIONS AMOUNT
  if (state.couponKey && state.coupon) {
    // Check usability
    let couponUsable = true;

    if (state.coupon.expirationDateTime) {
      couponUsable = couponUsable && (new Date().toISOString() < state.coupon.expirationDateTime);
    }

    let allConditionsSatisfied = true;
    for (let couponConditionItem of state.coupon.couponConditions) {
      if (couponConditionItem.conditionKey === "MEASKITSIZEEXISTS") {
        let measKitSizeExists = checkIfMeasKitSizeExists(state, userBodyMeasurementsDict);

        allConditionsSatisfied = allConditionsSatisfied && measKitSizeExists;
      }
    }
    couponUsable = couponUsable && allConditionsSatisfied;

    // Terbebas coupons can require a minimum spend (post-volume subtotal,
    // same basis the server validates against).
    if (state.coupon.minSubtotal !== undefined && subTotal < state.coupon.minSubtotal) {
      couponUsable = false;
    }

    // Use coupon
    if (couponUsable) {
      for (let key in state.coupon.couponEffects) {
        const couponEffect = state.coupon.couponEffects[key];
        if (couponEffect.effectKey === "PRICECUT") {
          // Never below zero — matches the server's cap at the subtotal.
          subTotal -= Math.min(subTotal, couponEffect.effectValue as number);
        }
        if (couponEffect.effectKey === "PRICECUTPERCENT") {
          // Rounded the same way the server computes the coupon amount.
          subTotal = subTotal - Math.round(subTotal * (couponEffect.effectValue as number));
        }
        if (couponEffect.effectKey === "SINGLEITEMDISCOUNT") {
          subTotal = subTotal - (cartItemsWithCouponPromotions?.[Object.keys(cartItemsWithCouponPromotions)?.[0]]?.amountDiscounted ?? 0);
        }
      }
    }
  }

  // PUT IN DISC WITH PROMOTIONS AMOUNT
  resultObj.totalAmountObj.discountedPriceAmountWithPromotions = subTotal;

  // CALC DISC WITH PROMOTIONS AMOUNT AND SHIPMENT
  if (state.shipmentPrice) {
    let endShipmentPrice = getEndShipmentPrice(getSubsidyItems(state, productListState), state.shipmentPrice, state?.coupon);
    subTotal = subTotal + (endShipmentPrice as number);
  }
  
  // PUT IN DISC WITH PROMOTIONS AMOUNT AND SHIPMENT
  resultObj.totalAmountObj.discountedPriceAmountWithPromotionsAndShipment = subTotal;

  return resultObj;
}

export function getDiscountedPriceAmountWithPromotionsAndShipment(state: CartState, productListState: ProductsList, 
  userBodyMeasurementsDict: {[key:string]: UserBodyMeasurements[]}, 
  userClothingMeasurementsDict: {[key: string]: {
      [key: string]: UserClothTypeSpecificBodyMeasurements[];
  }}) {
  let resultObj = calculateCompleteCartInvoice(state, productListState, userBodyMeasurementsDict, userClothingMeasurementsDict);

  return resultObj.totalAmountObj.discountedPriceAmountWithPromotionsAndShipment;
}

export function getNumberOfItems(state: CartState) {
  let total = 0;
  for (let itemKey in state.cartProductList) {
    total += state.cartProductList[itemKey].amount;
  }
  return total;
}

// Per-store shipping rules (replaces fareast's hardcoded subsidy table).
// Mirrors terbebas-backend computeChargedShipping — the server recomputes
// this at checkout, so keep both in sync.
function couponGivesFreeShipping(coupon: Coupon | undefined): boolean {
  return !!coupon?.freeShipping
    && (!coupon.expirationDateTime || new Date().toISOString() < coupon.expirationDateTime);
}

// Expands the cart into one subsidy item per pcs (name + original + selling
// price) — the input to the tenant subsidy function. Mirrors the server's
// expansion in order.service so client display matches the charged amount.
export function getSubsidyItems(state: CartState, productListState: ProductsList): ShippingSubsidyItem[] {
  const items: ShippingSubsidyItem[] = [];
  for (const cartItem of state.cartProductList) {
    const product = productListState.productsDictionary[cartItem.productId];
    const name = product?.name ?? '';
    let originalPrice: number;
    let subtotalPrice: number;
    if (cartItem.variantId && cartItem.unitPrice !== undefined) {
      originalPrice = cartItem.compareAtPrice ?? cartItem.unitPrice;
      subtotalPrice = cartItem.unitPrice;
    } else {
      originalPrice = product?.originalPrice ?? 0;
      subtotalPrice = product?.discountedPrice != null && product.discountedPrice !== -1
        ? product.discountedPrice : (product?.originalPrice ?? 0);
    }
    for (let n = 0; n < cartItem.amount; n++) items.push({ name, originalPrice, subtotalPrice });
  }
  return items;
}

// What the shopper pays for shipping, given the quoted courier rate and the
// per-pcs subsidy items. Coupon free-shipping wins over everything.
export function getEndShipmentPrice(items: ShippingSubsidyItem[], initialShipmentPrice: number | undefined, coupon?: Coupon) {
  if (!initialShipmentPrice || initialShipmentPrice === -1) {
    return initialShipmentPrice;
  }
  if (couponGivesFreeShipping(coupon)) return 0;
  const subsidy = calculateShippingSubsidyClient(items, coupon?.couponKey ?? '');
  return Math.max(0, initialShipmentPrice - subsidy);
}

// Display helper for the "POTONGAN ONGKIR" banner: the subsidy amount the
// tenant function grants for the current cart.
export function getShipmentSubsidyDisplay(items: ShippingSubsidyItem[], coupon?: Coupon):
  { type: 'free' | 'cut' | 'none', amount: number } {
  if (couponGivesFreeShipping(coupon)) return { type: 'free', amount: 0 };
  const subsidy = calculateShippingSubsidyClient(items, coupon?.couponKey ?? '');
  return subsidy > 0 ? { type: 'cut', amount: subsidy } : { type: 'none', amount: 0 };
}

export function getCurrentShipmentPromo() {
  const month = ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus",
  "September","Oktober","November","Desember"];
  
  const d = new Date();
  
  const shipmentPromoObject = {
    bulan: month[d.getMonth()],
    tahun: d.getFullYear(),
    bagianBulan: d.getDate() > 15 ? "akhir" : "launch",
    tanggalAkhir: d.getDate() > 15 ? (new Date(d.getFullYear(), d.getMonth() + 1, 0)).getDate() : 15,
  }

  return shipmentPromoObject;
}

export function getAddToCartNextSteps(product: ProductOnUser, measurement: UserBodyMeasurements | UserClothTypeSpecificBodyMeasurements) {
  const nextSteps: string[] = []; // if there is no next steps, the product can be added to cart right away.

  // TODO:
  // Actual consideration and steps to be done, determined by the product details
  // and the measurement requirements it has.
  return nextSteps;
}

export function getMeasurementsUsedInCart(state: CartState) {
  let results: {
    clothingType: ClothingType,
    fitType: FitType,
    creationDateTime: string,
  }[] = [];

  for (let key in state.cartProductList) {
    const cartItem = state.cartProductList[key];
    results.push({
      clothingType: cartItem.clothingType,
      fitType: cartItem.fitType,
      creationDateTime: cartItem.measurementCreationDateTime,
    })
  }

  return results;
}

// Returns "ENOUGH", "NOTENOUGH", or "LOADING"
export function canAddProductAmountToCart(state: CartState, cartStateItem: CartStateItem): "ENOUGH" | "NOTENOUGH" | "LOADING" | undefined {

  var cartStateItemHash = hash(cartStateItem);

  if (state.canAddItemDict[cartStateItemHash] !== undefined) {
    if (state.canAddItemDict[cartStateItemHash] === "LOADING") {
      return "LOADING";
    } else if (typeof state.canAddItemDict[cartStateItemHash] === "object") {
      return (state.canAddItemDict[cartStateItemHash] as any).canBeProvisioned ? "ENOUGH" : "NOTENOUGH";
    } else {
      return "LOADING";
    }

  } else {
    return "LOADING";
  }
}

// Action creators are generated for each case reducer function
export const { addItemCount, removeItemCount, removeItem, updateCourierInOrder, resetCourier, setInsuranceSelected,
  updateAddressInOrder, updateOrderState, resetCartState, resetCartStateAndSetOrderStatus,
  resetCartAddressAndPaymentState, updateUserTrigger, removeCurrentCoupon, removePaymentToken, setLocalCoupon, setProductSpotlightList, loadCartFromLocalStorage } = cartSlice.actions

export default cartSlice.reducer